// @ts-check

// This script is intended to work as a small helper used to translate static strings (text that is
// not handled by the CMS).
// Unlike other React specific packages, this helper can work anywhere.
// TODO: As of 2023-03-28 there are no ideal pre-made intl packages that work well/easily with
// Hydrogen 1, consider revisiting this translation approach in the future.

/**
 * Locales are inferred from env variables, see the `.env.development` file for more information.
 * Also see how `currentLocale` is being computed in this file in order to understand how variables
 * are being used.
 * @constant
 */
export const Locales = Object.freeze({
  English: 'en_gb',
  Japanese: 'ja_jp',
});

/**
 * @constant
 */
const allLocales = Object.values(Locales);

/**
 * @type {(typeof allLocales)[number]}
 */
// @ts-ignore
export const currentLocale = `${import.meta.env.VITE_DEFAULT_MARKET_LANGUAGE}_${
  import.meta.env.VITE_MARKET_FLAG
}`.toLowerCase();

if (!allLocales.includes(currentLocale)) {
  throw new Error(
    `Unsupported locale "${currentLocale}", did you set the correct env variables?`,
  );
}

// TODO: place in separate js or json files once objects start getting bigger. Keep in mind that
// json files would only work on server components (if we want to avoid lazy loading).
/**
 * @type {Record<'en_gb'|'ja_jp', Record<string, string>>}
 */
const translations = {
  [Locales.English]: {
    // Main Menu - TODO: Fix translations
    'mainMenu.men': 'Men',
    'mainMenu.women': 'Women',
    'mainMenu.stories': 'Stories',
    'mainMenu.information': 'Information',
    'mainMenu.subscribeForUpdates': 'Subscribe for Updates',
    // Menu bar (menu, search, bag)
    'menuBar.menu': 'Menu',
    'menuBar.search': 'Search',
    'menuBar.bag': 'Bag',
    'menuBar.bagClose': 'Close',
    // Cart (empty, full)
    'cart.empty': 'Your bag is empty',
    'cart.hasItem': 'Your bag',
    'cart.productSingular': 'Product',
    'cart.productPlural': 's',
    'cart.total': 'Total',
    'cart.checkout': 'Checkout Now',
    'cart.logIn': 'Log In',
    'cart.signUp': 'Sign Up',
    'cart.account': 'Account',
    'cart.logOut': 'Logout',

    // Sizes
    'sizes.xxs': 'Extra extra small',
    'sizes.xs': 'Extra small',
    'sizes.s': 'Small',
    'sizes.m': 'Medium',
    'sizes.l': 'Large',
    'sizes.xl': 'Extra large',
    'sizes.xxl': 'Extra extra large',

    // Collection Pages (highligths, categories, featured, refine)
    'collectionPages.highlights': 'Highlights',
    'collectionPages.categories': 'Categories',
    'collectionPages.featured': 'Featured',
    'collectionPages.refine': 'Refine',
    // Login Page
    'loginPage.logIn': 'Login',
    'loginPage.signIn': 'Sign In',
    'loginPage.shopName': 'New to Lavenham?',
    'loginPage.emailAddress': 'Email address',
    'loginPage.next': 'Next',
    'loginPage.changeEmail': 'Change email',
    'loginPage.password': 'Password',
    'loginPage.createAccount': 'Create An Account',
    'loginPage.forgot': 'Forgot password',
    'loginPage.error':
      ' Sorry we did not recognize either your email or password. Please try to sign in again or create a new account.',
    'loginPage.emailError': 'Please enter a valid email',
    'loginPage.passwordError': 'Passwords must be at least 8 characters',
    // Forgot Password Page
    'resetPasswordPage.forgotPassword': 'Forgot Password',
    'restPasswordPage.recoverPassword': 'Recover password',
    'resetPasswordPage.requestRestLink': 'Request Reset Link',
    'resetPasswordPage.instructions':
      'Enter the email address associated with your account to receive a link to reset your password.',
    'resetPasswordPage.requestSent': 'Request Sent.',
    'resetPasswordPage.confirmationMessage':
      'If that email address is in our system, you will receive an email with instructions about how to reset your password in a few minutes.',

    // Account Page
    'accountPage.title': 'Welcome to your account',
    'accountPage.welcomeMessage': 'Welcome to your account',
    'accountPage.noOrderHistory': "You haven't placed any orders yet.",
    'accountPage.startShopping': 'Start Shopping',
    'accountPage.settings': 'Account Settings',
    'accountPage.logoutButton': 'Logout',
    'accountPage.accountDetails': 'Account Details',
    'accountPage.orderHistory': 'Order History',
    'accountPage.addressBook': 'Address Book',
    'accountPage.noAddressBook': "You haven't saved any addresses yet.",
    'accountPage.addAddress': 'Add an Address',

    // Account Details
    'accountDetails.profile': 'Profile & Security',
    'accountDetails.editProfile': 'Edit',
    'accountDetails.name': 'Name',
    'accountDetails.addName': 'Add name',
    'accountDetails.contact': 'Contact',
    'accountDetails.addContact': 'Add mobile',
    'accountDetails.emailAddress': 'Email address',
    'accountDetails.password': 'Password',

    // update account details
    'accountDetailsUpdate.updateProfile': 'Update your profile',
    'accountDetailsUpdate.changePassword': 'Change your password',
    'accountDetailsUpdate.firstName': 'First Name',
    'accountDetailsUpdate.lastName': 'Last Name',
    'accountDetailsUpdate.mobile': 'Mobile',
    'accountDetailsUpdate.emailAddress': 'Email address',
    'accountDetailsUpdate.currentPassword': 'Current password',
    'accountDetailsUpdate.newPassword': 'New password',
    'accountDetailsUpdate.newPasswordRenter': 'Re-enter new password',
    'accountDetailsUpdate.passwordRule':
      'Password must be at least 8 characters.',
    'accountDetailsUpdate.save': 'Save',
    'accountDetailsUpdate.cancel': 'Cancel',
    'accountDetailsUpdate.saveError': 'Error saving account. Please try again.',

    // address update/add

    'addressDetailsEdit.addAddress': 'Add address',
    'addressDetailsEdit.editExistingAddress': 'Edit Address',
    'addressDetailsEdit.deleteExisting': 'Remove',
    'addressDetailsEdit.firstName': 'First name',
    'addressDetailsEdit.lastName': 'Last name',
    'addressDetailsEdit.company': 'Company',
    'addressDetailsEdit.addressLine1': 'Address line 1',
    'addressDetailsEdit.addressLine2': 'Address line 2',
    'addressDetailsEdit.city': 'City',
    'addressDetailsEdit.state': 'State / Province',
    'addressDetailsEdit.zipcode': 'Zip / Postal Code',
    'addressDetailsEdit.country': 'Country',
    'addressDetailsEdit.phone': 'Phone',
    'addressDetailsEdit.addressError': 'Address is required',
    'addressDetailsEdit.saveError': 'Error saving address. Please try again.',
    'addressDetailsEdit.passwordsNoMatch':
      'The two passwords entered did not match',
    'addressDetailsEdit.incorrectPassword': 'Current password is incorrect',
    'addressDetailsEdit.defaultAddress': 'Set as defualt address',
    'addressDetailsEdit.save': 'Save',
    'addressDetailsEdit.cancel': 'Cancel',
    'addressDetailsEdit.editExisting': 'Edit',
    'addressDetailsEdit.deleteExisiting': 'Remove',
    'addressDetailsEdit.defaultAddressLabel': 'Default',
    'addressDetailsEdit.provinceError': 'Province is not valid.',

    // order history
    'orderHistory.orderDetailTitle': 'Order detail',
    'orderHistory.orderTitle': 'Order',
    'orderHistory.orderNumber': 'Order No.',
    'orderHistory.returnToAccount': 'Return to Account Overview',
    'orderHistory.orderDate': 'Order Date',
    'orderHistory.fulfillmentStatus': 'Fulfillment Status',
    'orderHistory.viewDetails': 'View Details',
    'orderHistory.placedOn': 'Placed on',
    'orderHistory.productTitle': 'Product',
    'orderHistory.priceTitle': 'Price',
    'orderHistory.quantityTitle': 'Quantity',
    'orderHistory.quantityTitleTrunc': 'Qty:',
    'orderHistory.totalTitle': 'Total',
    'orderHistory.totalTitleWithTax': 'Total',
    'orderHistory.discountsTitle': 'Discounts',
    'orderHistory.subtotalTitle': 'Subtotal',
    'orderHistory.taxTitle': 'Tax',
    'orderHistory.shippingAddressTitle': 'Shipping Address',
    'orderHistory.noShippingAddressTitle': 'No shipping address defined',
    'orderHistory.statusTitle': 'Status',

    // Account Delete Address

    'addressDelete.title': 'Confirm removal',
    'addressDelete.description':
      'Are you sure you wish to remove this address?',
    'addressDelete.confirmButton': 'Confirm',
    'addressDelete.cancelButton': 'Cancel',
    // Mailing List/NewsLetter

    'mailingList.title': 'Join our Mailing List',
    'mailingList.description1':
      'By signing up, we will process your information in line with our',
    'mailingList.privacyPolicyLink': 'Privacy Policy',
    'mailingList.description2':
      '. Our newsletter will contain details of store openings, new products, collaborations, events etc. You can unsubscribe at any time.',
    'mailingList.consent': 'I agree',
    'mailingList.emailAddressLabel': 'Your Email Address',
    'mailingList.exampleEmailAddress': 'you@youremail.com',
    'mailingList.subscribe': 'Subscribe',
    'mailingList.emailAddress': 'Email Address',
    'mailingList.success': 'Success!',
    'mailingList.successText': 'Thank you for your subscription',
    'mailingList.error':
      'There was a problem subscribing. Please check your email has been entered correctly',

    // MISC REPEATED TRANSLATIONS:
    'button.close': 'Close',
    'button.back': 'Back',
    'button.menu': 'Menu',
    'errorsProducts.noProducts': 'No products found in this   collection',
    'browse.catalog': 'Browse catalog',
    'articles.noArticlesFound': 'No articles found',
    'articles.readTheStory': 'Read the Story',
    taxIncludedPrice: 'Tax included',
    'search.noResultsFound': 'No results found for ',

    // Categories menu
    // Keep it in English?

    // Featured menu
    'sortSelector.featured': 'Featured',
    'sortSelector.popular': 'Popular',
    'sortSelector.latest': 'Latest',

    // Refine/filter Menu
    'headerContent.refine': 'Refine',
    'headerContent.filters': 'Filters',
    'filters.filters': 'Filters',
    'filters.genderMen': 'Mens',
    'filters.genderWomen': 'Womens',
    // certain filter translations are not used below, but remain for continuity and possible future use.
    'filters.productType': 'Product Type',
    'filters.Size': 'Size',
    'filters.Feature': 'Feature',
    'filters.Wadding': 'Wadding',
    'filters.Fit': 'Fit',
    'filters.Quilting': 'Quilting',
    'filters.Colour': 'Colour',
    'filters.Collaboration': 'Collaboration',
    'filtersButton.back': 'Back',
    'filtersButton.clear': 'Clear',
    'filtersButton.close': 'Close',
    // Product card
    'productCard.details': 'Details',
    'productCard.sizing': 'Sizing',
    'productCard.delivery': 'Delivery',
    'productCard.addToBag': 'Add to bag',
    'productCard.selectSize': 'Please select a size',
    'productCard.buyNow': 'Buy now',
    'productCard.soldOut': 'Sold out',
    // Create an account form
    'createAccount.title': 'Create An Account.',
    'createAccount.email': 'Email address',
    'createAccount.password': 'Password',
    'createAccount.button': 'Create account',
    'createAccount.already': 'Already have an account?',
    'createAccount.login': 'Sign in',
    'createAccount.titleLeft': 'Register',
    'createAccount.enterPassword': 'Please enter a password',
    'createAccount.enterPasswordMin8': 'Password must be at least 8 characters',
    'createAccount.enterPasswordMax64':
      'Password must be less than 64 characters',
    'createAccount.enterPasswordConditions':
      'Password must contain at least one lowercase letter, one uppercase letter, one number and one special character',
    'createAccount.enterEmail': 'Please enter an email',
    'createAccount.enterValidEmail': 'Please enter a valid email',

    // status message (used on the orders page found in the utils.js file)
    'statusMessage.attemptedDelivery': 'Attempted delivery',
    'statusMessage.canceled': 'Canceled',
    'statusMessage.confirmed': 'Confirmed',
    'statusMessage.delivered': 'Delivered',
    'statusMessage.failure': 'Failure',
    'statusMessage.fulfilled': 'Fulfilled',
    'statusMessage.inProgress': 'In Progress',
    'statusMessage.inTransit': 'in transit',
    'statusMessage.labelPrinted': 'Label printed',
    'statusMessage.labelPurchased': 'Label purchased',
    'statusMessage.labelVoided': 'Label voided',
    'statusMessage.markedAsFulfilled': 'Marked as fulfilled',
    'statusMessage.notDelivered': 'Not delivered',
    'statusMessage.onHold': 'On Hold',
    'statusMessage.open': 'Open',
    'statusMessage.outForDelivery': 'Out for delivery',
    'statusMessage.partiallyFulfilled': 'Partially Fulfilled',
    'statusMessage.pendingFulfillment': 'Pending',
    'statusMessage.pickedUp': 'Displayed as Picked up',
    'statusMessage.readyForPickup': 'Ready for pickup',
    'statusMessage.restocked': 'Restocked',
    'statusMessage.scheduled': 'Scheduled',
    'statusMessage.submitted': 'Submitted',
    'statusMessage.unfulfilled': 'Unfulfilled',

    // Delivery Scheduling
    'delivery.title': 'DELIVERY SCHEDULING',
    'delivery.date': 'DATE',
    'delivery.time': 'TIME',
    'delivery.back': 'BACK',
    'delivery.clear': 'CLEAR',
    'delivery.select': 'CLOSE',
    'delivery.options.unselected': '',
    'delivery.options.optionOne': '',
    'delivery.options.optionTwo': '',
    'delivery.options.optionThree': '',
    'delivery.options.optionFour': '',
    'delivery.options.optionFive': '',
    // Customer Reciept
    'customerReciept.title': 'CUSTOMER RECIEPT',
    'customerReciept.options.yes': 'YES',
    'customerReciept.options.no': 'NO',
    // Breadcrumbs
    'breadcrumbs.men': 'MENS',
    'breadcrumbs.women': 'WOMENS',
    'breadcrumbs.clothing': 'CLOTHING', // Change
    'breadcrumbs.mens-classics': 'MENS CLASSICS',
    'breadcrumbs.mens-gilets': 'MENS GILETS',
    'breadcrumbs.mens-jackets': 'MENS JACKETS',
    'breadcrumbs.mens-other-styles': 'MENS OTHER STYLES',
    'breadcrumbs.mens-collaborations': 'MENS COLLABORATIONS',
    'breadcrumbs.womens-classics': 'WOMENS CLASSICS',
    'breadcrumbs.womens-gilets': 'WOMENS GILETS',
    'breadcrumbs.womens-jackets': 'WOMENS JACKETS',
    'breadcrumbs.womens-other-styles': 'WOMENS OTHER STYLES',
    'breadcrumbs.womens-collaborations': 'WOMENS COLLABORATIONS',
    'breadcrumbs.products': 'PRODUCTS',
    'breadcrumbs.allProducts': 'ALL PRODUCTS',
    'breadcrumbs.collections': 'COLLECTIONS',
    'breadcrumbs.stories': 'STORIES',
    // Blog Post
    'blogPost.shopTheEdit': 'Shop the Edit—',
    'blogPost.madeInSuffolk': 'More from the Made In Suffolk series—',
    // Product Page
    'productPage.alsoLike': 'You might also like—',
    'productPage.notFound': 'TAKE ME TO THE HOME PAGE',
    'productPage.notFoundHeading': 'We’ve lost this product',
    'productPage.notFoundDescr':
      'We couldn’t find the product you’re looking for. Try checking the URL or heading back to the home page.',
    // Search
    'quickSearch.placeholder': "I'm looking for...",
    'quickSearch.noItems': 'No items found.',
    'quickSearch.showAllItems': 'Show all',
    'quickSearch.results': 'results',
    'quickSearch.topResults': 'Top Results',
    'quickSearch.searchResultsFor': 'Search results for',
    'quickSearch.categories': 'Categories',
    'quickSearch.products': 'Products',
    // Active Account
    'active.title': 'Activate Account.',
    'active.subtitle': 'Create your password to activate your account.',
    'active.placeholder': 'Password',
    'active.placeholderRe': 'Re-enter password',
    'active.save': 'Save',
    // Reset password
    'reset.title': 'Reset Password.',
    'reset.subtitle': 'Enter a new password for your account.',
    'reset.password': 'Password',
    'reset.reenter': 'Re-enter password',
    'reset.save': 'save',
  },
  [Locales.Japanese]: {
    // Reset password
    'reset.title': 'パスワード再設定',
    'reset.subtitle': '新しいパスワードを設定してください。',
    'reset.password': 'パスワード',
    'reset.reenter': 'パスワード再入力',
    'reset.save': '保存',
    // Active Account
    'active.title': 'アカウントの有効化',
    'active.subtitle':
      '新規パスワードを設定してアカウントを有効にしてください。',
    'active.placeholder': 'パスワード入力',
    'active.placeholderRe': 'パスワードの再入力',
    'active.save': '保存',
    // Main Menu - TODO: Fix translations
    'mainMenu.men': 'メンズ',
    'mainMenu.women': 'ウィメンズ',
    'mainMenu.stories': 'ストーリー',
    'mainMenu.information': 'インフォメーション',
    'mainMenu.subscribeForUpdates': 'ニュースレターに登録',
    // Menu bar (menu, search, bag)
    'menuBar.menu': 'メニュー',
    'menuBar.search': 'サーチ',
    'menuBar.bag': 'カート',
    'menuBar.bagClose': '閉じる',
    // Cart (empty, full)
    'cart.empty': 'カートには何も入っていません。',
    'cart.hasItem': 'カート',
    'cart.productSingular': '商品',
    'cart.productPlural': ' ',
    'cart.total': '合計',
    'cart.checkout': 'ご注文手続きに進む',
    'cart.logIn': 'ログイン',
    'cart.signUp': '新規会員登録',
    'cart.account': 'アカウント',
    'cart.logOut': 'ログアウト',

    // Sizes
    'sizes.xxs': 'XXS',
    'sizes.xs': 'XS',
    'sizes.s': 'S',
    'sizes.m': 'M',
    'sizes.l': 'L',
    'sizes.xl': 'XL',
    'sizes.xxl': 'XXL',

    // Collection Pages (highligths, categories, featured, refine)
    'collectionPages.highlights': 'ハイライト',
    'collectionPages.categories': 'カテゴリー',
    'collectionPages.featured': '特集',
    'collectionPages.refine': '絞り込み',
    // Login Page
    'loginPage.logIn': 'ログイン',
    'loginPage.signIn': 'メールアドレス入力',
    'loginPage.shopName': 'ログインIDをお持ちでない方?',
    'loginPage.emailAddress': 'メールアドレス',
    'loginPage.changeEmail': 'メール変更',
    'loginPage.next': '進む',
    'loginPage.password': 'パスワード',
    'loginPage.createAccount': '新規会員登録',
    'loginPage.forgot': 'パスワードをお忘れですか？',
    'loginPage.emailError': 'ログインIDが違います。',
    'loginPage.passwordError': 'パスワードが違います',
    'loginPage.error': 'ログインIDまたはパスワードが違います。',
    // Forgot Password Page
    'resetPasswordPage.forgotPassword': 'パスワードをお忘れの方',
    'resetPasswordPage.recoverPassword': 'パスワード再設定', // need translation
    'resetPasswordPage.requestRestLink': '送信する',
    'resetPasswordPage.instructions':
      'メールアドレスを入力して「送信」ボタンを押してください。登録されているメールアドレス宛にパスワードが送信されます。送信されたメールにURLが記載されていますので、URLをクリックしパスワード変更画面からパスワードを変更してください。',
    'resetPasswordPage.requestSent': 'リクエストが送信されました。',
    'resetPasswordPage.confirmationMessage':
      'ご登録頂いているメールアドレス宛てにパスワード再設定用URLを送信させていただきました。メール記載のURLより、パスワードの再設定をお願いいたします。',

    // Account Page
    'accountPage.title': 'マイページ',
    'accountPage.welcomeMessage': 'ようこそ',
    'accountPage.noOrderHistory': '購入履歴はありません。',
    'accountPage.startShopping': 'ショッピングを続ける',
    'accountPage.settings': 'アカウント設定',
    'accountPage.logoutButton': 'ログアウト',
    'accountPage.accountDetails': '会員情報',
    'accountPage.orderHistory': '購入履歴一覧',
    'accountPage.addressBook': '住所',
    'accountPage.noAddressBook': '登録済み住所はございません。',
    'accountPage.addAddress': 'アドレスを追加する',

    // Account Details
    'accountDetails.profile': 'プロフィール',
    'accountDetails.editProfile': '変更',
    'accountDetails.name': '名前',
    'accountDetails.addName': '名前の追加',
    'accountDetails.contact': '電話番号',
    'accountDetails.addContact': '電話番号の追加',
    'accountDetails.emailAddress': 'メールアドレス',
    'accountDetails.password': 'パスワード',

    // update account details
    'accountDetailsUpdate.updateProfile': '会員情報変更',
    'accountDetailsUpdate.changePassword': 'パスワードを変更する',
    'accountDetailsUpdate.firstName': '名',
    'accountDetailsUpdate.lastName': '姓',
    'accountDetailsUpdate.mobile': '電話番号（例：+819012345678）',
    'accountDetailsUpdate.emailAddress': 'メールアドレス',
    'accountDetailsUpdate.currentPassword': '現在のパスワード',
    'accountDetailsUpdate.newPassword': '新パスワード',
    'accountDetailsUpdate.newPasswordRenter': '新しいパスワードの再入力',
    'accountDetailsUpdate.passwordRule': '※ 半角英数字8文字以上の混在入力',
    'accountDetailsUpdate.save': '保存する',
    'accountDetailsUpdate.cancel': 'キャンセル',
    'accountDetailsUpdate.saveError':
      'アカウントの保存に失敗しました。もう一度やり直してください。',

    // order History
    'orderHistory.orderDetailTitle': '注文情報',
    'orderHistory.orderTitle': '注文',
    'orderHistory.orderNumber': '注文番号',
    'orderHistory.returnToAccount': 'アカウント概要に戻る',
    'orderHistory.orderDate': '受注日',
    'orderHistory.fulfillmentStatus': 'フィルミングの状況',
    'orderHistory.viewDetails': '詳細を見る',
    'orderHistory.placedOn': '受信日',
    'orderHistory.productTitle': '商品名',
    'orderHistory.priceTitle': '価格',
    'orderHistory.quantityTitle': '数量',
    'orderHistory.quantityTitleTrunc': '数量:',
    'orderHistory.totalTitle': '合計',
    'orderHistory.totalTitleWithTax': '合計金額（税込）',
    'orderHistory.discountsTitle': '割引情報',
    'orderHistory.subtotalTitle': '小計',
    'orderHistory.taxTitle': '税金',
    'orderHistory.shippingAddressTitle': '配送先住所',
    'orderHistory.noShippingAddressTitle': '発送先が決まっていない',
    'orderHistory.statusTitle': 'ステータス',

    // Address Update/Add

    'addressDetailsEdit.addAddress': 'アドレスの追加',
    'addressDetailsEdit.editExistingAddress': 'アドレスの変更',
    'addressDetailsEdit.firstName': '名（必須）',
    'addressDetailsEdit.lastName': '姓（必須）',
    'addressDetailsEdit.company': '会社',
    'addressDetailsEdit.addressLine1': '番地（必須）',
    'addressDetailsEdit.addressLine2': 'ビル・マンション名・部屋番号',
    'addressDetailsEdit.city': '市区町村（必須）',
    'addressDetailsEdit.state': '都道府県（必須）',
    'addressDetailsEdit.zipcode': '郵便番号（必須）',
    'addressDetailsEdit.country': '国（必須）',
    'addressDetailsEdit.phone': '電話番号',
    'addressDetailsEdit.addressError': '住所が必要です。',
    'addressDetailsEdit.saveError':
      'アドレスの保存にエラーが発生しました。もう一度やり直してください。',
    'addressDetailsEdit.passwordsNoMatch':
      '入力された2つのパスワードが一致しません。',
    'addressDetailsEdit.incorrectPassword':
      '現在のパスワードが正しくありません',
    'addressDetailsEdit.defaultAddress': 'デフォルトのアドレスに設定',
    'addressDetailsEdit.save': '保存する',
    'addressDetailsEdit.cancel': 'キャンセル',
    'addressDetailsEdit.editExisting': '変更',
    'addressDetailsEdit.deleteExisting': '消す',
    'addressDetailsEdit.defaultAddressLabel': 'デフォルト',
    'addressDetailsEdit.provinceError': '都道府県が有効ではありません',

    // Account Delete Address

    'addressDelete.title': '削除の確認',
    'addressDelete.description': 'このアドレスを削除しますか？',
    'addressDelete.confirmButton': '確認する',
    'addressDelete.cancelButton': 'キャンセル',

    // Mailing List/NewsLetter

    'mailingList.title': 'ニュースレターに登録する',
    'mailingList.description1': 'ご登録いただくことで、お客様が当社の',
    'mailingList.privacyPolicyLink': 'プライバシーポリシー',
    'mailingList.description2':
      'にご同意いただけたものといたします。ニュースレターには、店舗のオープン、新製品、コラボレーション、イベントなどの詳細が記載されています。また、配信はいつでも停止することができます。',
    'mailingList.consent': '承諾',
    'mailingList.emailAddressLabel': 'お客様のメールアドレス',
    'mailingList.exampleEmailAddress': 'you@youremail.com',
    'mailingList.subscribe': '登録する',
    'mailingList.emailAddress': 'メールアドレス',
    'mailingList.success': '登録完了',
    'mailingList.successText': 'ご登録ありがとうございました',
    'mailingList.error':
      '購読に問題がありました。メールが正しく入力されているか、ボックスにチェックが入っているかをご確認ください。',

    // MISC / REPEATED TRANSLATIONS:
    'button.close': '閉じる',
    'button.back': '戻る',
    'button.menu': 'メニュー',
    'errorsProducts.noProducts': 'このコレクションに該当する製品はありません',
    'browse.catalog': 'カタログを見る',
    'articles.noArticlesFound': '該当する記事はありません',
    'articles.readTheStory': 'ストーリーを読む',
    taxIncludedPrice: '税込',
    'search.noResultsFound': '該当する商品はありません',
    readMore: 'もっと読む',

    // Categories menu
    // Keep it in English?

    // Featured menu
    'sortSelector.featured': 'おすすめ順',
    'sortSelector.popular': '人気順',
    'sortSelector.latest': '新着順',
    // Refine/filter Menu
    'headerContent.refine': '絞り込み',
    'filters.filters': '絞り込み',
    'filters.genderMen': 'メンズ',
    'filters.genderWomen': 'ウィメンズ',

    // capitilised to match the calls from shopify. One remains in Japanese because shopify defualts this translation to Japanese when store location is changed.
    'filters.ProductType': 'アイテム',
    'filters.Size': 'サイズ',
    'filters.Feature': '特徴',
    'filters.Wadding': '中綿',
    'filters.Fit': 'フィット',
    'filters.Quilting': 'キルティング',
    'filters.Colour': 'カラー',
    'filters.Collaboration': 'コラボレーション',
    'filtersButton.back': '戻る',
    'filtersButton.clear': 'クリア',
    'filtersButton.close': '終了',
    // Product card
    'productCard.details': '商品説明',
    'productCard.sizing': 'サイズガイド',
    'productCard.delivery': '配送と交換について',
    'productCard.addToBag': 'カートに入れる',
    'productCard.selectSize': 'サイズを選択してください',
    'productCard.buyNow': '今すぐ購入する',
    'productCard.soldOut': '在庫なし',

    // Create an account form
    'createAccount.title': '新規会員登録',
    'createAccount.email': 'メールアドレス',
    'createAccount.password': 'パスワード',
    'createAccount.button': '入力内容を確認する',
    'createAccount.already': 'アカウント登録済みのお客様',
    'createAccount.login': 'ログイン',
    'createAccount.titleLeft': '登録',
    'createAccount.enterPassword': 'パスワードを入力してください',
    'createAccount.enterPasswordMin8': '8文字以上でご入力ください',
    'createAccount.enterPasswordMax64': '64文字以下でご入力ください',
    'createAccount.enterPasswordConditions':
      'パスワードは英大文字、小文字、特殊文字、数字の組み合わせで作成してください',
    'createAccount.enterEmail': 'メールアドレスを入力してください',
    'createAccount.enterValidEmail': '有効なメールアドレスを入力してください',

    // status message (used on the orders page found in the utils.js file)
    'statusMessage.attemptedDelivery': '配信未遂',
    'statusMessage.canceled': 'キャンセルされました。',
    'statusMessage.confirmed': '確認済み',
    'statusMessage.delivered': '配信',
    'statusMessage.failure': '失敗例',
    'statusMessage.fulfilled': 'フルフィルド',
    'statusMessage.inProgress': '進行中',
    'statusMessage.inTransit': '途中経過',
    'statusMessage.labelPrinted': 'ラベル印刷',
    'statusMessage.labelPurchased': '購入したレーベル',
    'statusMessage.labelVoided': 'ラベルの無効化',
    'statusMessage.markedAsFulfilled': '満たされたとしてマークされる',
    'statusMessage.notDelivered': '未配信',
    'statusMessage.onHold': 'オンホールド',
    'statusMessage.open': 'オープン',
    'statusMessage.outForDelivery': '配信終了',
    'statusMessage.partiallyFulfilled': '一部実施済み',
    'statusMessage.pendingFulfillment': '保留中',
    'statusMessage.pickedUp': '拾ったように表示',
    'statusMessage.readyForPickup': 'ピックアップの準備',
    'statusMessage.restocked': '再製',
    'statusMessage.scheduled': '予定',
    'statusMessage.submitted': '提出された',
    'statusMessage.unfulfilled': '未完成',

    // Featured menu

    // Refine Menu

    // Delivery Scheduling
    'delivery.title': 'お届け希望日時',
    'delivery.date': '日付',
    'delivery.time': '時分',
    'delivery.back': '戻る',
    'delivery.clear': '削除する',
    'delivery.select': '選択する',
    'delivery.options.unselected': '希望なし',
    'delivery.options.optionOne': '午前中',
    'delivery.options.optionTwo': '12-14時',
    'delivery.options.optionThree': '14-16時',
    'delivery.options.optionFour': '16-18時',
    'delivery.options.optionFive': '18-21時',
    // Customer Reciept
    'customerReciept.title': '納品書同梱',
    'customerReciept.options.yes': 'はい',
    'customerReciept.options.no': 'いいえ',
    // Breadcrumbs
    'breadcrumbs.men': 'メンズ',
    'breadcrumbs.women': 'ウィメンズ',
    'breadcrumbs.clothing': 'ウェア', // Change
    'breadcrumbs.mens-classics': 'MENS CLASSICS',
    'breadcrumbs.mens-gilets': 'MENS GILETS',
    'breadcrumbs.mens-jackets': 'MENS JACKETS',
    'breadcrumbs.mens-other-styles': 'MENS OTHER STYLES',
    'breadcrumbs.mens-collaborations': 'MENS COLLABORATIONS',
    'breadcrumbs.womens-classics': 'WOMENS CLASSICS',
    'breadcrumbs.womens-gilets': 'WOMENS GILETS',
    'breadcrumbs.womens-jackets': 'WOMENS JACKETS',
    'breadcrumbs.womens-other-styles': 'WOMENS OTHER STYLES',
    'breadcrumbs.womens-collaborations': 'WOMENS COLLABORATIONS',
    'breadcrumbs.products': 'PRODUCTS',
    'breadcrumbs.allProducts': 'すべてのアイテム',
    'breadcrumbs.collections': 'コレクション',
    'breadcrumbs.stories': 'ストーリーズ',
    // Blog Post
    'blogPost.shopTheEdit': '関連アイテム',
    'blogPost.madeInSuffolk': 'メイド・イン・サフォークシリーズをさらに詳しく-',
    // Product Page
    'productPage.alsoLike': 'こちらもおすすめ',
    'productPage.notFoundBtn': 'トップページに戻る',
    'productPage.notFoundHeading': 'ページが見つかりません',
    'productPage.notFoundDescr':
      'クリックしたリンクが古い、または入力したURLにスペルミスが発生している可能性があります。ご確認の上、もう一度お試しいただくか、以下のリンクのどれかをクリックしてください。',

    // Headers
    'header.Fallback': 'ホーム',
    'header.Stories': 'ストーリーズ',
    'header.Men': 'メンズ',
    'header.MensClassics': 'メンズ クラシックス',
    'header.MensGilets': 'メンズ ジレ',
    'header.MensJackets': 'メンズ ジャケット',
    'header.MensOtherStyles': 'メンズ アザーズスタイルズ',
    'header.MensCollaborations': 'メンズ コラボレーション',
    'header.Women': 'ウィメンズ',
    'header.WomensClassics': 'ウィメンズ クラシックス',
    'header.WomensGilets': 'ウィメンズ ジレ',
    'header.WomensJackets': 'ウィメンズ ジャケット',
    'header.WomensOtherStyles': 'ウィメンズ アザーズスタイルズ',
    'header.WomensCollaborations': 'ウィメンズ コラボレーション',
    // Search
    'quickSearch.placeholder': '何をお探しですか？',
    'quickSearch.noItems': '該当する項目はありません。',
    'quickSearch.showAllItems': 'すべて検索',
    'quickSearch.results': '結果',
    'quickSearch.topResults': 'マッチした検索結果',
    'quickSearch.searchResultsFor': 'の検索結果',
    'quickSearch.categories': 'カテゴリー',
    'quickSearch.products': 'アイテム',
  },
};

/**
 * @param {(typeof allLocales)[number]} locale
 * @returns {Record<string,string>}
 */
const getMessages = (locale) => {
  const messages = translations[locale];
  if (!messages) {
    console.error(`Could not find messages for locale "${currentLocale}"`);
    return {};
  }
  return messages;
};

/**
 * Returns the equivalent translation for a given "key". Locale is detected automatically based on
 * the environment variables. If key does not have a respective message, the same key will be
 * returned for informational purposes.
 * @param {string} key
 * @returns {string}
 */
export const t = (key) => {
  const messages = getMessages(currentLocale);
  const m = messages[key];
  if (!m) {
    console.error(`Could not find translation for "${key}"`);
    return key;
  }
  return m;
};
